/**
 * Color helpers for the Buying Table.
 *
 * Extracted from BuyingTable.vue so each branch can be unit-tested without
 * instantiating the giant Vue component.
 */

/**
 * Resolve the Vuetify theme color for the "Suggested Quantity" chip on a
 * buying-table row.
 *
 * Customer feedback (RPM's TESLA / Kristi, 2026-04-07): the chip needs to
 * separate "act now" rows from "needs attention soon" rows. The earlier
 * flat-red default surfaced reorder rows but did not communicate urgency.
 * The new behavior is a 3-state gradient driven by the row's reorder due
 * date.
 *
 * Branch order (first match wins):
 *   1. suggested <= 0     → 'grey'    (no reorder needed — fine)
 *   2. daysToReorder <= 1 → 'error'   (red — due today, past, or tomorrow)
 *   3. otherwise          → 'warning' (orange — needs attention, not urgent)
 *
 * Note on Vuetify color names: in the default Vuetify 2 theme `'error'`
 * renders red and `'warning'` renders orange — the more-alarming-sounding
 * name maps to the LESS urgent state here. The color choice tracks the
 * theme palette, not the lexical severity of the name.
 *
 * Missing or unparseable reorderDate is treated as overdue (daysToReorder = 0)
 * so a row with no anchor still surfaces as red rather than silently degrading
 * to orange.
 */
export const getSuggestedQtyColor = (item: any): string => {
  const suggested = Number(item['[inventory_replenishment]']) || 0;
  if (suggested <= 0) { return 'grey'; }

  const daysToReorder = daysUntilReorder(item.reorderDate);
  if (daysToReorder <= 1) { return 'error'; }
  return 'warning';
};

const MS_PER_DAY = 86400000;

/**
 * Days from today (midnight local) until the given date string. Returns:
 *   - negative for past dates
 *   - 0 for today
 *   - 1 for tomorrow
 *   - 0 for missing / null / empty / unparseable dates (treated as today/overdue)
 *
 * TZ note: bare 'YYYY-MM-DD' date strings (the format the report API sends)
 * are parsed as UTC midnight by JavaScript's spec, then `setHours(0,0,0,0)`
 * normalizes them to local midnight on whichever local date contains that
 * UTC instant. This is consistent across CI (UTC) and the dev team's
 * timezones (UTC+0..+11). Hosts at UTC-12 or further west would see the
 * date shift by one calendar day; if that becomes a real CI environment,
 * switch to explicit local-date parsing.
 */
const daysUntilReorder = (dateStr: string | null | undefined): number => {
  if (!dateStr) { return 0; }
  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) { return 0; }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / MS_PER_DAY);
};
